/**
 * Configuration 페이지 공용 레이아웃 셸
 * ConnectDevice 카드, 기기 불일치 배너, 액션 버튼, 저장/로드 모달 등
 * LITE/MINI가 동일하게 사용하는 골격을 담는다. 설정 그리드는 children으로 받는다.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Button, Card } from '@/components/ui';
import { ConnectDevice } from '@/components/device/connectDevice';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { DeviceMismatchBanner } from '@/components/configuration/DeviceMismatchBanner';
import { ConfigListModal } from '@/components/configuration/ConfigListModal';
import { ConfigNameInputModal } from '@/components/configuration/ConfigNameInputModal';
import { TopSection, ActionButtons, ButtonLeft } from '@/pages/configuration/ConfigurationStyles';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

interface ConfigPageShellProps {
  deviceType: DeviceType;
  detectedDeviceType: DeviceType | null;
  expectedDeviceType: DeviceType;
  isDeviceConnected: boolean;
  isCompatible: boolean;
  /** ConnectDevice 크래들 연결 허용 여부 (MINI는 false) */
  cradleAble?: boolean;

  // 연결 콜백
  onDeviceLoaded: (deviceInfo: any, deviceConfig: any) => void;
  onConnectionChange: (connectionType: any) => void;
  onDisconnected: () => void;

  // 액션 버튼 동작 + 활성화 조건
  onSaveToStorage: () => void;
  onLoadFromStorage: () => void;
  onLoadDefault: () => void;
  onSave: () => void;
  canSaveOrLoad: boolean;
  canLoadFromStorage: boolean;

  // 모달
  configListModalVisible: boolean;
  onConfigSelect: (config: any) => void;
  onConfigListClose: () => void;
  nameInputModalVisible: boolean;
  onNameConfirm: (name: string) => void;
  onNameInputClose: () => void;

  /** 설정 그리드 (General + Sensor 카드) */
  children: React.ReactNode;
}

export const ConfigPageShell: React.FC<ConfigPageShellProps> = ({
  deviceType,
  detectedDeviceType,
  expectedDeviceType,
  isDeviceConnected,
  isCompatible,
  cradleAble,
  onDeviceLoaded,
  onConnectionChange,
  onDisconnected,
  onSaveToStorage,
  onLoadFromStorage,
  onLoadDefault,
  onSave,
  canSaveOrLoad,
  canLoadFromStorage,
  configListModalVisible,
  onConfigSelect,
  onConfigListClose,
  nameInputModalVisible,
  onNameConfirm,
  onNameInputClose,
  children,
}) => {
  const { t } = useTranslation('config');

  // 기본값 로드는 LITE/MINI 동일 조건 (연결 + 호환)
  const canLoadDefault = isDeviceConnected && isCompatible;

  return (
    <>
      <DefaultContents>
        <Card padding={6} height="auto" data-tour="config-connect-device">
          <ConnectDeviceWrapper>
            <ConnectDevice
              deviceType={deviceType}
              cradleAble={cradleAble}
              onDeviceLoaded={onDeviceLoaded}
              onConnectionChange={onConnectionChange}
              onDisconnected={onDisconnected}
            />
          </ConnectDeviceWrapper>
        </Card>

        {isDeviceConnected && detectedDeviceType && !isCompatible && (
          <DeviceMismatchBanner
            detectedType={detectedDeviceType}
            expectedType={expectedDeviceType}
          />
        )}

        <TopSection>
          <ActionButtons>
            <ButtonLeft>
              <Button
                data-tour="config-save-to-storage"
                variant="tertiaryNeutral"
                onClick={onSaveToStorage}
                disabled={!canSaveOrLoad}
                width="179px"
              >
                {t('saveToStorage')}
              </Button>
              <Button
                data-tour="config-load-from-storage"
                variant="tertiaryNeutral"
                onClick={onLoadFromStorage}
                disabled={!canLoadFromStorage}
                width="186px"
              >
                {t('loadFromStorage')}
              </Button>
              <Button
                data-tour="config-load-default"
                variant="tertiaryNeutral"
                onClick={onLoadDefault}
                disabled={!canLoadDefault}
                width="186px"
              >
                {t('loadDefaultSettings')}
              </Button>
            </ButtonLeft>

            <Button
              data-tour="config-save-settings"
              variant="primary"
              onClick={onSave}
              disabled={!canSaveOrLoad}
            >
              {t('saveSettings')}
            </Button>
          </ActionButtons>
        </TopSection>

        {children}
      </DefaultContents>

      <ConfigListModal
        isVisible={configListModalVisible}
        isMini={deviceType === DeviceType.MINI}
        onSelect={onConfigSelect}
        onClose={onConfigListClose}
      />
      <ConfigNameInputModal
        isVisible={nameInputModalVisible}
        onConfirm={onNameConfirm}
        onClose={onNameInputClose}
      />
    </>
  );
};

const ConnectDeviceWrapper = styled.div`
  width: 100%;
  max-width: 558px;
`;
